import * as React from "react";
import { CircularProgress, Skeleton, Typography, TypographyProps, styled, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import * as Atoms from "./atoms";

const useStyles = makeStyles(() => {
    return {
        spinner: { display: "block", marginLeft: "auto", marginRight: "auto", width: "fit-content" },
        container: {
            animationDuration: "2s",
            animationName: "$fade",
            animationFillMode: "forwards",
        },
        "@keyframes fade": {
            from: {
                opacity: 0,
            },
            to: {
                opacity: 1,
            },
        },
    };
});

const CardTitle: React.FunctionComponent<{
    titleProps?: TypographyProps;
    actions?: React.ReactNode;
    hero?: boolean;
    justifyContent?: string;
    children?: React.ReactNode;
    skeleton?: boolean;
}> = (props) => {
    const { titleProps, actions, hero, justifyContent, children, skeleton } = props;
    const theme = useTheme();

    const StyledGridContainer = styled(Atoms.GridContainer)({
        borderRadius: `${Atoms.BORDER_RADIUS}px ${Atoms.BORDER_RADIUS}px 0px 0px`,
        paddingBottom: 0,
        backgroundColor: hero ? theme.palette.primary.main : "",
    });

    if (skeleton) {
        return (
            <StyledGridContainer>
                <Atoms.Grid
                    container
                    alignItems="center"
                    justifyContent={justifyContent ? justifyContent : "space-between"}
                >
                    <Atoms.Grid item xs={hero ? 12 : 2}>
                        <Typography {...titleProps}>
                            <Skeleton />
                        </Typography>
                    </Atoms.Grid>
                    {actions ? <Atoms.Grid item>{actions}</Atoms.Grid> : undefined}
                </Atoms.Grid>
            </StyledGridContainer>
        );
    }

    return (
        <StyledGridContainer>
            <Atoms.Grid
                container
                alignItems="center"
                justifyContent={justifyContent ? justifyContent : "space-between"}
            >
                <Atoms.Grid item xs={hero ? 12 : undefined}>
                    <Atoms.Title
                        color={hero ? "secondary" : undefined}
                        align={hero ? "center" : undefined}
                        {...titleProps}
                    >
                        {children}
                    </Atoms.Title>
                </Atoms.Grid>
                {actions ? <Atoms.Grid item>{actions}</Atoms.Grid> : undefined}
            </Atoms.Grid>
        </StyledGridContainer>
    );
};

const Spinner: React.FunctionComponent<{
    localizedMessageKey?: string;
    noFade?: boolean;
}> = (props) => {
    const { localizedMessageKey, noFade } = props;
    const classes = useStyles();
    return (
        <div
            className={clsx({
                [classes.container]: !noFade,
            })}
        >
            <div className={classes.spinner}>
                <CircularProgress size={86} variant="indeterminate" />
            </div>
            <Atoms.Spacer />
            <Suspense fallback={<Typography variant="h5" align="center"></Typography>}>
                <LocalizedMessage localizedMessageKey={localizedMessageKey} />
            </Suspense>
        </div>
    );
};

const LocalizedMessage: React.FunctionComponent<{ localizedMessageKey?: string }> = (props) => {
    const { localizedMessageKey } = props;
    const { t } = useTranslation();
    return (
        <Typography variant="h5" align="center">
            {localizedMessageKey ? t(localizedMessageKey) : ""}
        </Typography>
    );
};

export { CardTitle, Spinner };
